import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/generals/logo'
import { ButtonLink } from '@/components/generals/button'
import { navLinks } from '@/constants/site'

export const Navbar: FC = (): ReactNode => {
    const [open, setOpen] = useState(false)
    const [hidden, setHidden] = useState(false)
    const [scrolled, setScrolled] = useState(false)
    const [active, setActive] = useState('')

    // Slide away while scrolling down, come back when scrolling up.
    useEffect(() => {
        let last = window.scrollY
        let frame = 0

        const update = (): void => {
            frame = 0
            const y = window.scrollY
            setScrolled(y > 24)
            if (Math.abs(y - last) > 6) {
                setHidden(y > last && y > 240)
                last = y
            }
        }

        const onScroll = (): void => {
            if (!frame) frame = requestAnimationFrame(update)
        }

        window.addEventListener('scroll', onScroll, { passive: true })
        return () => {
            window.removeEventListener('scroll', onScroll)
            cancelAnimationFrame(frame)
        }
    }, [])

    // Highlight the link of the section currently in view.
    useEffect(() => {
        const sections = navLinks
            .map((link) => document.getElementById(link.href.replace('/#', '')))
            .filter((section): section is HTMLElement => section !== null)

        if (sections.length === 0) return

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) setActive(`/#${entry.target.id}`)
            })
        }, { rootMargin: '-45% 0px -50% 0px' })

        sections.forEach((section) => observer.observe(section))
        return () => observer.disconnect()
    }, [])

    useEffect(() => {
        if (!open) return

        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') setOpen(false)
        }

        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [open])

    const close = (): void => setOpen(false)

    return (
        <header
            className={cn(
                'fixed inset-x-0 top-0 z-50 px-3 pt-3 transition-[translate] duration-500 ease-[cubic-bezier(0.2,0.7,0.2,1)] sm:px-5 sm:pt-4 animate-in fade-in slide-in-from-top-6 duration-700',
                hidden && !open && '-translate-y-[130%]'
            )}
        >
            <nav
                aria-label='Main'
                className={cn(
                    'mx-auto flex max-w-6xl items-center justify-between gap-4 rounded-full border-2 border-plum py-2 pr-2 pl-5 backdrop-blur-md transition-all duration-300 sm:pl-6',
                    scrolled ? 'bg-cream/95 shadow-[0_6px_0_0_var(--color-plum)]' : 'bg-cream/80 shadow-[0_4px_0_0_var(--color-plum)]'
                )}
            >
                <Link href='/' onClick={close} className='group rounded-full'>
                    <Logo className='transition-transform duration-300 group-hover:-rotate-6 group-hover:scale-110' />
                    <span className='sr-only'>Home</span>
                </Link>

                <ul className='hidden items-center gap-1 lg:flex'>
                    {navLinks.map((link) => (
                        <li key={link.href}>
                            <Link
                                href={link.href}
                                aria-current={active === link.href ? 'true' : undefined}
                                className={cn(
                                    'relative inline-block rounded-full px-4 py-2 font-heading text-lg tracking-wide text-plum transition-all duration-200 hover:-translate-y-0.5 hover:-rotate-2 hover:bg-butter',
                                    active === link.href && 'bg-plum text-cream hover:bg-plum hover:text-cream'
                                )}
                            >
                                {link.label}
                            </Link>
                        </li>
                    ))}
                </ul>

                <div className='flex items-center gap-2'>
                    <ButtonLink href='/#contact' size='sm' className='hidden sm:inline-flex'>
                        Let's talk
                    </ButtonLink>

                    <button
                        type='button'
                        aria-expanded={open}
                        aria-controls='mobile-menu'
                        onClick={() => setOpen((value) => !value)}
                        className='grid size-11 place-items-center rounded-full border-2 border-plum bg-butter text-plum lg:hidden'
                    >
                        {open ? <X className='size-5' /> : <Menu className='size-5' />}

                        <span className='sr-only'>
                            {open ? 'Close menu' : 'Open menu'}
                        </span>
                    </button>
                </div>
            </nav>

            <div
                id='mobile-menu'
                hidden={!open}
                className='mx-auto mt-3 max-w-6xl rounded-[2rem] border-2 border-plum bg-cream p-3 shadow-[0_4px_0_0_var(--color-plum)] animate-in fade-in zoom-in-95 slide-in-from-top-2 duration-300 lg:hidden'
            >
                <ul className='grid gap-1'>
                    {navLinks.map((link, index) => (
                        <li key={link.href} className='animate-in fade-in slide-in-from-left-4 fill-mode-both duration-300' style={{ animationDelay: `${index * 50}ms` }}>
                            <Link
                                href={link.href}
                                onClick={close}
                                className='block rounded-2xl px-4 py-3 font-heading text-2xl tracking-wide text-plum transition-colors hover:bg-butter'
                            >
                                {link.label}
                            </Link>
                        </li>
                    ))}
                </ul>

                <ButtonLink href='/#contact' onClick={close} className='mt-3 w-full'>
                    Let's talk
                </ButtonLink>
            </div>
        </header>
    )
}
